import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n';
import './ProjectDropdown.scss';

/**
 * Custom project dropdown with color indicators
 * @param {Object} props
 * @param {Array} props.projects - List of available projects
 * @param {string} props.value - Currently selected project ID
 * @param {Function} props.onChange - Callback when project is selected
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 */
function ProjectDropdown({ projects, value, onChange, disabled = false }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selectedProject = projects.find(p => p.id === value);

  // Get display name for a project (translates system project names)
  const getDisplayName = (project) => {
    if (!project) return '';
    return project.isSystem ? t('holiday.holidaysName') : project.name;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = useCallback((projectId) => {
    onChange(projectId);
    setIsOpen(false);
  }, [onChange]);

  return (
    <div className="project-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className="project-dropdown__trigger"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        {selectedProject ? (
          <>
            <span 
              className="project-dropdown__color" 
              style={{ backgroundColor: selectedProject.color }}
            />
            <span className="project-dropdown__name">{getDisplayName(selectedProject)}</span>
          </>
        ) : (
          <span className="project-dropdown__name project-dropdown__name--none">{t('project.select')}</span>
        )}
        <span className="project-dropdown__arrow">{isOpen ? '\u25B2' : '\u25BC'}</span>
      </button>
      {isOpen && (
        <div className="project-dropdown__menu">
          {projects.map(project => (
            <button
              key={project.id}
              type="button"
              className={`project-dropdown__option ${value === project.id ? 'project-dropdown__option--selected' : ''}`}
              onClick={() => handleSelect(project.id)}
            >
              <span 
                className="project-dropdown__color" 
                style={{ backgroundColor: project.color }}
              />
              <span className="project-dropdown__name">{getDisplayName(project)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default ProjectDropdown;
